import { Stack, Typography } from "@mui/material";
import { CellText } from "components/Omnisearch/helpComponents/CellText";
import { formatNotReportingReason } from "encamp-shared/src/submissions";
import { SubmissionEncampStatus, SubmissionStatus } from "generated-graphql/graphql";
import { STATUS_LABELS } from "utils/submissionStatusLabels";

type SubmissionStatusCellProps = {
  status?: SubmissionStatus | string | null;
  encampStatus?: SubmissionEncampStatus | string | null;
  isStaff?: boolean;
  notReportingReason?: string | null;
  notReportingReasonNote?: string | null;
};

const SENT_BACK_SX = { color: "warning.dark" };

function isInProgressStatus(value: SubmissionStatus | string): boolean {
  return value === SubmissionStatus.InProgress || value === "IN_PROGRESS";
}

function isNotReportingStatus(value: SubmissionStatus | string): boolean {
  return value === SubmissionStatus.NotReporting || value === "NOT_REPORTING";
}

function isRejectedEncampStatus(
  value: SubmissionEncampStatus | string | null | undefined
): boolean {
  if (value === null || value === undefined) {
    return false;
  }
  return value === SubmissionEncampStatus.Rejected || value === "REJECTED";
}

export function SubmissionStatusCell({
  status,
  encampStatus,
  isStaff,
  notReportingReason,
  notReportingReasonNote,
}: SubmissionStatusCellProps) {
  let statusValue: SubmissionStatus | string = SubmissionStatus.NotStarted;
  if (typeof status === "string") {
    if (status.trim()) {
      statusValue = status;
    }
  } else if (status) {
    statusValue = status;
  }

  const showSentBack =
    isStaff && isInProgressStatus(statusValue) && isRejectedEncampStatus(encampStatus);

  const label = showSentBack
    ? "Sent Back for Edits"
    : (STATUS_LABELS[statusValue] ?? String(statusValue));

  const reasonText = isNotReportingStatus(statusValue)
    ? formatNotReportingReason(notReportingReason, notReportingReasonNote)
    : null;

  if (!reasonText) {
    return (
      <CellText variant="body2" noWrap sx={showSentBack ? SENT_BACK_SX : undefined}>
        {label}
      </CellText>
    );
  }

  return (
    <Stack justifyContent="center" sx={{ height: "100%", overflow: "hidden", py: 1 }}>
      <Typography variant="body2" noWrap sx={showSentBack ? SENT_BACK_SX : undefined}>
        {label}
      </Typography>
      <Typography variant="caption" color="text.secondary" noWrap title={reasonText}>
        {reasonText}
      </Typography>
    </Stack>
  );
}
